import React from 'react';
import { Briefcase, Calendar, Coffee, TrendingUp } from 'lucide-react';
import { Button } from '../UI/Button';
import { useTranslation } from 'react-i18next';
import { getLanguagePathPrefix } from '../../i18n';
import { KombyLogo } from '../Branding/KombyLogo';
import { useTheme } from '../../contexts/ThemeContext';
import { getHeroSideImage, getHeroVideoSrc } from '../../config/heroMedia';
import { useDeferredHeroVideo } from '../../hooks/useDeferredHeroVideo';

export const HeroOffice: React.FC = () => {
  const { t } = useTranslation(['common', 'office']);
  const { isBlack } = useTheme();
  const playVideo = useDeferredHeroVideo(!isBlack);

  return (
    <section className="py-20 relative overflow-hidden">
      {!isBlack && playVideo && (
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="none"
          className="absolute inset-0 w-full h-full object-cover z-0"
        >
          <source src={getHeroVideoSrc('office')} type="video/mp4" />
        </video>
      )}
      <div className={`absolute inset-0 z-[1] ${isBlack ? 'bg-transparent' : 'bg-white/60 dark:bg-gray-900/60'}`} />

      <div className="relative z-[2] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-xl h-14 w-14">
                <Briefcase className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="flex items-center h-14">
                <KombyLogo className="h-14 w-auto" animate={false} />
              </div>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              {t('office:seo.h1', { defaultValue: t('office:hero.title') })}
            </h1>

            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
              {t('office:hero.description')}
            </p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="flex items-center space-x-3">
                <Calendar className="h-5 w-5 text-accent1" />
                <span className="text-gray-700 dark:text-gray-300">{t('office:features.appointments')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Coffee className="h-5 w-5 text-accent1" />
                <span className="text-gray-700 dark:text-gray-300">{t('office:features.projects')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <TrendingUp className="h-5 w-5 text-accent1" />
                <span className="text-gray-700 dark:text-gray-300">{t('office:features.invoicing')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Briefcase className="h-5 w-5 text-accent1" />
                <span className="text-gray-700 dark:text-gray-300">{t('office:features.clientTracking')}</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button variant="glossy" size="lg" className="rounded-full px-8" onClick={() => {
                window.location.href = `${getLanguagePathPrefix()}/pricing`;
              }}>
                {t('common:cta.freeTrial')}
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => {
                  window.location.href = `${getLanguagePathPrefix()}/contact/`;
                }}
              >
                {t('common:cta.viewDemo')}
              </Button>
            </div>
          </div>

          <div className="relative hidden lg:block">
            <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={getHeroSideImage('office')}
                alt={t('office:images.professionalWorking')}
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
                width={800}
                height={384}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />

              <div className="absolute top-6 right-6 bg-white/90 backdrop-blur-sm rounded-lg p-4 shadow-lg">
                <div className="text-center">
                  <div className="text-2xl font-bold text-accent1">1000+</div>
                  <div className="text-sm text-gray-600">{t('office:stats.professionals')}</div>
                </div>
              </div>

              <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur-sm rounded-lg p-4 shadow-lg">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">98%</div>
                  <div className="text-sm text-gray-600">{t('common:stats.satisfaction')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
